import type { Appointment } from "@/features/clinic/types/appointment";
import type {
  Department,
  DepartmentCandidatesInput,
  DepartmentFollowingPicksInput,
  NarrowAppointmentPicksInput,
  NarrowedAppointmentPicks,
} from "@/features/clinic/types/department";
import type { ReportFilterOption } from "@/features/reports/common/types";

import { REPORT_FILTER_ANY } from "@/features/reports/common/constants/list-filters";

/** The sentinel for bookings in no department - not a department id. */
export const DEPARTMENT_NONE = "none";

/**
 * The one option list every department filter reads - the appointments page
 * header and the appointment drilldown bar - so they can never offer
 * different departments.
 */
export const departmentFilterOptions = (
  departments: Pick<Department, "id" | "name">[] = [],
): ReportFilterOption[] => [
  { key: REPORT_FILTER_ANY, label: "All departments" },
  ...departments.map((department) => ({
    key: department.id,
    label: department.name,
  })),
  { key: DEPARTMENT_NONE, label: "No department" },
];

const keepIfAny = <T>(all: T[], kept: T[]): T[] => (kept.length ? kept : all);

const keepSelected = <T extends { id: string }>(
  list: T[],
  all: T[],
  id?: string | null,
): T[] => {
  if (!id || list.some((item) => item.id === id)) return list;
  const selected = all.find((item) => item.id === id);

  return selected ? [...list, selected] : list;
};

/*
 * Each pick on the booking form narrows the other two, never locks them: a
 * filter that would empty a list is ignored, and whatever is already picked
 * stays listed even when it falls outside the narrowing. The treatment is the
 * strongest signal for the department, the doctor second - same order as the
 * report derivation on the API. That rule governs the three pickers below.
 */

/**
 * The departments that could own this booking: the treatment's, narrowed by the
 * doctor's. Either filter is skipped when it would leave nothing, because a
 * treatment or doctor filed under no department says nothing about where the
 * booking belongs - it does not say "nowhere". Exactly one candidate is an
 * answer; two is the Botox case, where only a person can say which. The API's
 * `appointmentDepartmentUnsettled` applies the same two filters.
 */
export const departmentCandidates = ({
  departments,
  doctorId,
  treatmentId,
}: DepartmentCandidatesInput): Department[] => {
  const byTreatment = treatmentId
    ? departments.filter((d) => d.treatments.some((t) => t.id === treatmentId))
    : [];
  const base = byTreatment.length ? byTreatment : departments;
  const byDoctor = doctorId
    ? base.filter((d) => d.doctors.some((doc) => doc.id === doctorId))
    : [];

  return byDoctor.length ? byDoctor : base;
};

/**
 * What the department field should hold once the doctor and treatment have
 * spoken: the one department they leave, else the current pick while it still
 * fits, else nothing. Whether a pick may be overwritten at all is the caller's
 * business, not this function's.
 */
export const departmentFollowingPicks = ({
  departments,
  doctorId,
  treatmentId,
  current = "",
}: DepartmentFollowingPicksInput): string => {
  // Nothing loaded yet says nothing about the pick - leave it alone.
  if (!departments.length) return current;

  const candidates = departmentCandidates({
    departments,
    doctorId,
    treatmentId,
  });

  if (candidates.length === 1) return candidates[0].id;

  return candidates.some((d) => d.id === current) ? current : "";
};

export const narrowAppointmentPicks = ({
  departments,
  doctors = [],
  treatments = [],
  departmentId,
  doctorId,
  treatmentId,
}: NarrowAppointmentPicksInput): NarrowedAppointmentPicks => {
  const candidates = departmentCandidates({
    departments,
    doctorId,
    treatmentId,
  });

  const department = departments.find((d) => d.id === departmentId);
  const doctor = doctors.find((d) => d.id === doctorId);

  const narrowedDoctors = department?.doctors.length
    ? keepIfAny(
        doctors,
        doctors.filter((d) =>
          department.doctors.some((doc) => doc.id === d.id),
        ),
      )
    : doctors;

  let narrowedTreatments = doctor?.treatments?.length
    ? keepIfAny(
        treatments,
        treatments.filter((t) =>
          doctor.treatments?.some((dt) => dt.id === t.id),
        ),
      )
    : treatments;

  if (department?.treatments.length) {
    narrowedTreatments = keepIfAny(
      narrowedTreatments,
      narrowedTreatments.filter((t) =>
        department.treatments.some((dt) => dt.id === t.id),
      ),
    );
  }

  return {
    departments: keepSelected(candidates, departments, departmentId),
    doctors: keepSelected(narrowedDoctors, doctors, doctorId),
    treatments: keepSelected(narrowedTreatments, treatments, treatmentId),
    nextDepartmentId: departmentFollowingPicks({
      departments,
      doctorId,
      treatmentId,
      current: departmentId ?? "",
    }),
  };
};

/**
 * The department a booking belongs to, for display: the one stored on it,
 * else the one its treatment (then doctor) leaves no doubt about, else null -
 * which is exactly when the outcome flow asks.
 */
export const resolveAppointmentDepartment = (
  appointment: Pick<Appointment, "departmentId" | "doctorId" | "treatmentId">,
  departments: Department[],
): Department | null => {
  const stored = departments.find((d) => d.id === appointment.departmentId);

  if (stored) return stored;

  const candidates = departmentCandidates({
    departments,
    doctorId: appointment.doctorId,
    treatmentId: appointment.treatmentId,
  });

  // Two candidates is as unanswered as none: the booking is in one of them and
  // nobody has said which. The API agrees - see `appointmentDepartmentUnsettled`.
  return candidates.length === 1 ? candidates[0] : null;
};

export type MissingAppointmentDetail = "treatment" | "department";

/**
 * The one thing to go and answer, or null when nothing is owed.
 *
 * Never both: a booking with no treatment usually has no department either,
 * but only because of the treatment - naming the department as a second
 * failure would be reporting one gap twice, and asking for it before the
 * treatment is answered asks for something the treatment is about to settle.
 * So the treatment comes first, and the department is only owed once a
 * treatment is on the row and still leaves it open. Read live off the row and
 * the same resolver the Department column uses, so the two can never
 * disagree; the flow asks exactly this. A clinic without departments is never
 * told it is missing one.
 */
export const missingAppointmentDetail = (
  appointment: Pick<Appointment, "departmentId" | "doctorId" | "treatmentId">,
  departments: Department[],
): MissingAppointmentDetail | null => {
  if (!appointment.treatmentId) return "treatment";

  return departments.length > 0 &&
    !resolveAppointmentDepartment(appointment, departments)
    ? "department"
    : null;
};
